import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiSave, FiDownload, FiUpload, FiUser, FiClock,
  FiCheckCircle, FiXCircle, FiAlertTriangle, FiFileText, FiCreditCard, FiCalendar, FiHash
} from 'react-icons/fi';
import { refundsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { getDocumentFileSizeError } from '../../utils/uploadLimits';

const STATUS_OPTIONS = [
  { value: 'pending_approval', label: 'Pending Approval', icon: <FiClock size={14} />, color: '#856404', bg: '#fef3cd' },
  { value: 'under_review', label: 'Under Review', icon: <FiFileText size={14} />, color: '#004085', bg: '#cce5ff' },
  { value: 'approved', label: 'Approved', icon: <FiCheckCircle size={14} />, color: '#155724', bg: '#d4edda' },
  { value: 'rejected', label: 'Rejected', icon: <FiXCircle size={14} />, color: '#721c24', bg: '#f8d7da' },
  { value: 'processed', label: 'Processed', icon: <FiCreditCard size={14} />, color: '#0c5460', bg: '#d1ecf1' },
  { value: 'completed', label: 'Completed', icon: <FiCheckCircle size={14} />, color: '#155724', bg: '#c3e6cb' },
  { value: 'on_hold', label: 'On Hold', icon: <FiAlertTriangle size={14} />, color: '#383d41', bg: '#e2e3e5' }
];
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: '#6c757d' },
  { value: 'medium', label: 'Medium', color: '#fd7e14' },
  { value: 'high', label: 'High', color: '#dc3545' }
];
const REFUND_METHODS = [
  { value: '', label: 'Select Method' },
  { value: 'fly_wire', label: 'Flywire' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'instalment', label: 'Instalment' },
  { value: 'other', label: 'Other' }
];

const statusColors = {
  pending_approval: { bg: '#fef3cd', color: '#856404' },
  under_review: { bg: '#cce5ff', color: '#004085' },
  approved: { bg: '#d4edda', color: '#155724' },
  rejected: { bg: '#f8d7da', color: '#721c24' },
  processed: { bg: '#d1ecf1', color: '#0c5460' },
  completed: { bg: '#c3e6cb', color: '#155724' },
  on_hold: { bg: '#e2e3e5', color: '#383d41' }
};

const RefundRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = String(user?.role || '').toLowerCase();
  const isAdmin = ['super_admin', 'admin', 'sales_manager'].includes(role);

  const [refund, setRefund] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);

  const [form, setForm] = useState({
    status: '', priority: '', refund_date: '', refund_method: '',
    reference_no: '', internal_notes: '', rejected_reason: ''
  });

  const loadRefund = useCallback(async () => {
    try {
      const res = await refundsAPI.getById(id);
      const r = res.data.refund;
      setRefund(r);
      setHistory(res.data.history || []);
      setForm({
        status: r.status || 'pending_approval',
        priority: r.priority || 'medium',
        refund_date: r.refund_date ? String(r.refund_date).slice(0, 10) : '',
        refund_method: r.refund_method || '',
        reference_no: r.reference_no || '',
        internal_notes: r.internal_notes || '',
        rejected_reason: r.rejected_reason || ''
      });
      window.dispatchEvent(new Event('refund-mark-read'));
    } catch (err) {
      toast.error('Failed to load refund details');
      navigate('/refund-requests');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { loadRefund(); }, [loadRefund]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('status', form.status);
      fd.append('priority', form.priority);
      if (form.refund_date) fd.append('refund_date', form.refund_date);
      if (form.refund_method) fd.append('refund_method', form.refund_method);
      fd.append('reference_no', form.reference_no || '');
      fd.append('internal_notes', form.internal_notes || '');
      if (form.status === 'rejected') fd.append('rejected_reason', form.rejected_reason || '');
      if (receiptFile) fd.append('refund_receipt_copy_file', receiptFile);

      await refundsAPI.update(id, fd);
      toast.success('Refund request updated');
      setReceiptFile(null);
      await loadRefund();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
    setSaving(false);
  };

  const openReceipt = async () => {
    try {
      const res = await refundsAPI.getReceiptUrl(id);
      window.open(res.data.url, '_blank');
    } catch (_) {
      toast.error('Could not open receipt');
    }
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!refund) return null;

  const sc = statusColors[refund.status] || statusColors.on_hold;

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/refund-requests')}><FiArrowLeft /></button>
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              Refund Request #{refund.refund_id}
              <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: sc.bg, color: sc.color }}>
                {(refund.status || '').replace(/_/g, ' ').toUpperCase()}
              </span>
            </h1>
            <p className="page-subtitle">
              {refund.student_ref} — {refund.title || ''} {refund.first_name} {refund.last_name}
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr', gap: 20 }}>
        {/* Left: Request Info */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Request Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Refund Type</div>
              <div style={{ fontWeight: 600 }}>{String(refund.refund_type || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Amount</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--primary)' }}>
                {refund.currency} {parseFloat(refund.amount_refunded || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Requested By</div>
              <div style={{ fontWeight: 500 }}>{refund.initiated_by_name || refund.created_by_name || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Requested At</div>
              <div>{refund.created_at ? new Date(refund.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Priority</div>
              <div style={{ fontWeight: 700, color: { high: '#dc3545', medium: '#fd7e14', low: '#6c757d' }[refund.priority] || '#6c757d' }}>
                {(refund.priority || 'medium').toUpperCase()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Student</div>
              <button className="btn btn-sm btn-outline" onClick={() => navigate(`/students/${refund.student_id}`)}>
                {refund.student_ref} — View
              </button>
            </div>
            {refund.description && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Description</div>
                <div>{refund.description}</div>
              </div>
            )}
            {refund.notes && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Notes</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{refund.notes}</div>
              </div>
            )}
            {refund.refund_receipt_copy && (
              <div style={{ gridColumn: '1 / -1' }}>
                <button className="btn btn-outline btn-sm" onClick={openReceipt}>
                  <FiDownload size={14} /> View Receipt
                </button>
              </div>
            )}
            {refund.rejected_reason && (
              <div style={{ gridColumn: '1 / -1', padding: 12, background: '#f8d7da', borderRadius: 8, border: '1px solid #f5c6cb' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#721c24', marginBottom: 4 }}>Rejection Reason</div>
                <div style={{ color: '#721c24' }}>{refund.rejected_reason}</div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Admin Processing Panel */}
        {isAdmin && (() => {
          const activeStatus = STATUS_OPTIONS.find((s) => s.value === form.status) || STATUS_OPTIONS[0];
          const activePriority = PRIORITY_OPTIONS.find((p) => p.value === form.priority) || PRIORITY_OPTIONS[1];
          return (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Panel header with current status accent */}
              <div style={{
                padding: '16px 20px', borderBottom: '1px solid var(--border-color)',
                background: `linear-gradient(135deg, ${activeStatus.bg}44, ${activeStatus.bg}22)`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 28, height: 28, borderRadius: 8, background: activeStatus.bg, color: activeStatus.color
                    }}>{activeStatus.icon}</span>
                    Process Request
                  </h3>
                  <span style={{
                    padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    background: activePriority.color + '18', color: activePriority.color, border: `1px solid ${activePriority.color}33`
                  }}>
                    {activePriority.label} Priority
                  </span>
                </div>
              </div>

              <div style={{ padding: 20 }}>
                {/* Status & Priority row */}
                <div style={{
                  marginBottom: 20, padding: 14, borderRadius: 10,
                  background: 'var(--bg, #f8fafc)', border: '1px solid var(--border-light, #f1f5f9)'
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-muted)', marginBottom: 12 }}>
                    Status & Priority
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Status <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <select
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        style={{ borderColor: activeStatus.color + '66', background: activeStatus.bg + '44' }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Priority</label>
                      <select
                        value={form.priority}
                        onChange={(e) => setForm({ ...form, priority: e.target.value })}
                        style={{ borderColor: activePriority.color + '55' }}
                      >
                        {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment Details section */}
                <div style={{
                  marginBottom: 20, padding: 14, borderRadius: 10,
                  background: 'var(--bg, #f8fafc)', border: '1px solid var(--border-light, #f1f5f9)'
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiCreditCard size={13} /> Payment Details
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiCalendar size={12} /> Refund Date
                      </label>
                      <input
                        type="date"
                        value={form.refund_date}
                        onChange={(e) => setForm({ ...form, refund_date: e.target.value })}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Method</label>
                      <select
                        value={form.refund_method}
                        onChange={(e) => setForm({ ...form, refund_method: e.target.value })}
                      >
                        {REFUND_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginTop: 12, marginBottom: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FiHash size={12} /> Reference Number
                    </label>
                    <input
                      type="text"
                      value={form.reference_no}
                      onChange={(e) => setForm({ ...form, reference_no: e.target.value })}
                      placeholder="Enter transaction reference..."
                    />
                  </div>
                </div>

                {/* Rejection Reason (conditional) */}
                {form.status === 'rejected' && (
                  <div style={{
                    marginBottom: 20, padding: 14, borderRadius: 10,
                    background: '#fef2f2', border: '1px solid #fecaca'
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#991b1b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiXCircle size={13} /> Rejection Reason
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ color: '#991b1b' }}>Reason <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <textarea
                        value={form.rejected_reason}
                        onChange={(e) => setForm({ ...form, rejected_reason: e.target.value })}
                        rows={3}
                        placeholder="Provide a clear reason for rejecting this request..."
                        style={{ borderColor: '#fecaca', background: '#fff' }}
                      />
                    </div>
                  </div>
                )}

                {/* Internal Notes */}
                <div style={{
                  marginBottom: 20, padding: 14, borderRadius: 10,
                  background: 'var(--bg, #f8fafc)', border: '1px solid var(--border-light, #f1f5f9)'
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiFileText size={13} /> Internal Notes
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Notes</label>
                    <textarea
                      value={form.internal_notes}
                      onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                      rows={3}
                      placeholder="Processing notes visible only to admins..."
                    />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FiAlertTriangle size={10} /> Not visible to the requester
                  </div>
                </div>

                {/* Receipt Upload */}
                <div style={{
                  marginBottom: 24, padding: 14, borderRadius: 10,
                  background: receiptFile ? '#f0fdf4' : 'var(--bg, #f8fafc)',
                  border: `1px solid ${receiptFile ? '#bbf7d0' : 'var(--border-light, #f1f5f9)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiUpload size={13} /> Receipt Upload
                  </div>
                  <div style={{
                    border: '2px dashed var(--input-border, #CBD5E1)', borderRadius: 10, padding: 20,
                    background: 'var(--white, #fff)', textAlign: 'center',
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      id="refund-detail-receipt"
                      type="file"
                      accept=".pdf,.jpg,.jpeg"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          const err = getDocumentFileSizeError(f);
                          if (err) { toast.error(err); e.target.value = ''; return; }
                        }
                        setReceiptFile(f || null);
                      }}
                    />
                    {receiptFile ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                        <FiCheckCircle size={20} style={{ color: '#16a34a' }} />
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>{receiptFile.name}</div>
                          <div style={{ fontSize: 11, color: '#4ade80' }}>Ready to upload</div>
                        </div>
                        <label htmlFor="refund-detail-receipt" className="btn btn-outline btn-sm" style={{ cursor: 'pointer', marginLeft: 8 }}>Change</label>
                      </div>
                    ) : (
                      <div>
                        <div style={{ marginBottom: 8 }}>
                          <FiUpload size={24} style={{ color: 'var(--text-light, #94a3b8)' }} />
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>
                          Drop receipt here or click to browse
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-light, #94a3b8)', marginBottom: 10 }}>
                          PDF, JPG, JPEG
                        </div>
                        <label htmlFor="refund-detail-receipt" className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
                          Choose File
                        </label>
                      </div>
                    )}
                  </div>
                  {refund.refund_receipt_copy && !receiptFile && (
                    <div style={{
                      marginTop: 10, padding: '8px 12px', borderRadius: 8,
                      background: '#ecfdf5', border: '1px solid #a7f3d0',
                      display: 'flex', alignItems: 'center', gap: 8
                    }}>
                      <FiCheckCircle size={14} style={{ color: '#16a34a', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 500, color: '#15803d', flex: 1 }}>Existing receipt on file</span>
                      <button className="btn btn-outline btn-sm" onClick={openReceipt} style={{ fontSize: 11, padding: '3px 10px' }}>
                        <FiDownload size={11} /> View
                      </button>
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <button
                  className="btn btn-primary"
                  disabled={saving}
                  onClick={handleSave}
                  style={{
                    width: '100%', padding: '12px 16px', fontSize: 14, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    borderRadius: 10
                  }}
                >
                  <FiSave size={16} /> {saving ? 'Saving Changes...' : 'Save Changes'}
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Timeline / History */}
      <div className="card" style={{ marginTop: 20 }}>
        <h3 style={{ marginBottom: 16 }}>Activity Timeline</h3>
        {history.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No activity recorded yet.</p>
        ) : (
          <div style={{ position: 'relative', paddingLeft: 24 }}>
            <div style={{ position: 'absolute', left: 7, top: 4, bottom: 4, width: 2, background: 'var(--border-color)' }} />
            {history.map((h, idx) => (
              <div key={h.audit_id || idx} style={{ position: 'relative', marginBottom: 20, paddingLeft: 20 }}>
                <div style={{
                  position: 'absolute', left: -5, top: 4, width: 12, height: 12, borderRadius: '50%',
                  background: idx === 0 ? 'var(--primary)' : 'var(--border-color)', border: '2px solid var(--white)'
                }} />
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {String(h.action || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 12, marginTop: 2 }}>
                  <span><FiUser size={11} /> {h.changed_by_name || 'System'}</span>
                  <span><FiClock size={11} /> {(h.changed_at || h.created_at) ? new Date(h.changed_at || h.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</span>
                </div>
                {h.new_values && (() => {
                  try {
                    const nv = typeof h.new_values === 'string' ? JSON.parse(h.new_values) : h.new_values;
                    if (nv && typeof nv === 'object') {
                      const keys = Object.keys(nv).filter((k) => !['created_at', 'updated_at', 'created_by', 'updated_by'].includes(k));
                      if (keys.length === 0) return null;
                      return (
                        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', padding: 8, borderRadius: 6 }}>
                          {keys.slice(0, 6).map((k) => (
                            <div key={k}><strong>{k.replace(/_/g, ' ')}:</strong> {String(nv[k] ?? '-')}</div>
                          ))}
                        </div>
                      );
                    }
                  } catch (_) {}
                  return null;
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RefundRequestDetail;
